import { SITE_TUNING } from "./siteTuning";

export type ClassroomDisplayMetrics = {
    devicePixelRatio: number;
    innerHeight: number;
    innerWidth: number;
    isExtended: boolean;
    screenHeight: number;
    screenWidth: number;
};

export type ClassroomDisplayTypographyState = {
    changed: boolean;
    enabled: boolean;
    metrics: ClassroomDisplayMetrics;
    scale: number;
};

type ExtendedScreen = Screen & {
    isExtended?: boolean;
};

const CLASSROOM_DISPLAY_CLASS = "is-classroom-display-typography-compensated";
const CLASSROOM_DISPLAY_FONT_SIZE_VARIABLE = "--ziar-classroom-display-root-font-size";

const isWithin = (value: number, minimum: number, maximum: number): boolean =>
    Number.isFinite(value) && value >= minimum && value <= maximum;

export const getClassroomDisplayTypographyScale = (metrics: ClassroomDisplayMetrics): number => {
    const tuning = SITE_TUNING.classroomDisplayTypography;
    const physicalWidth = metrics.screenWidth * metrics.devicePixelRatio;
    const physicalHeight = metrics.screenHeight * metrics.devicePixelRatio;
    const matchesCapturedClassroomPanel =
        metrics.isExtended &&
        isWithin(metrics.devicePixelRatio, tuning.minDevicePixelRatio, tuning.maxDevicePixelRatio) &&
        isWithin(metrics.innerWidth, tuning.minViewportWidthPx, tuning.maxViewportWidthPx) &&
        isWithin(metrics.innerHeight, tuning.minViewportHeightPx, tuning.maxViewportHeightPx) &&
        isWithin(physicalWidth, tuning.minPhysicalWidthPx, tuning.maxPhysicalWidthPx) &&
        isWithin(physicalHeight, tuning.minPhysicalHeightPx, tuning.maxPhysicalHeightPx);

    if (!matchesCapturedClassroomPanel) {
        return 1;
    }

    return Math.min(1, tuning.targetDevicePixelRatio / metrics.devicePixelRatio);
};

export const readClassroomDisplayMetrics = (): ClassroomDisplayMetrics => {
    const currentScreen = window.screen as ExtendedScreen;

    return {
        devicePixelRatio: window.devicePixelRatio,
        innerHeight: window.innerHeight,
        innerWidth: window.innerWidth,
        isExtended: currentScreen.isExtended === true,
        screenHeight: currentScreen.height,
        screenWidth: currentScreen.width
    };
};

export const syncClassroomDisplayTypography = (
    root: HTMLElement = document.documentElement,
    metrics: ClassroomDisplayMetrics = readClassroomDisplayMetrics()
): ClassroomDisplayTypographyState => {
    const scale = getClassroomDisplayTypographyScale(metrics);
    const enabled = scale < 1;
    const fontSize = enabled ? `${Math.round(scale * 10_000) / 100}%` : "";
    const changed =
        root.classList.contains(CLASSROOM_DISPLAY_CLASS) !== enabled ||
        root.style.getPropertyValue(CLASSROOM_DISPLAY_FONT_SIZE_VARIABLE) !== fontSize;

    root.classList.toggle(CLASSROOM_DISPLAY_CLASS, enabled);
    // Fit a desktop-width setup as one surface instead of shrinking just its text.
    const lobbyScale = enabled ? Math.min(1, metrics.innerWidth / SITE_TUNING.classroomDisplayTypography.hostLobbyWidthPx) : 1;
    root.style.setProperty("--ziar-classroom-display-scale", String(lobbyScale));

    if (enabled) {
        root.style.setProperty(CLASSROOM_DISPLAY_FONT_SIZE_VARIABLE, fontSize);
        root.dataset.classroomDisplayTypography = "compensated";
    } else {
        root.style.removeProperty(CLASSROOM_DISPLAY_FONT_SIZE_VARIABLE);
        delete root.dataset.classroomDisplayTypography;
    }

    if (changed) {
        console.info(
            `[display-layout] typography=${enabled ? "compensated" : "system"} ` +
                `dpr=${metrics.devicePixelRatio} viewport=${metrics.innerWidth}x${metrics.innerHeight} ` +
                `screen=${metrics.screenWidth}x${metrics.screenHeight} extended=${metrics.isExtended} ` +
                `root=${enabled ? fontSize : "100%"}`
        );
    }

    return { changed, enabled, metrics, scale };
};
